<template>
  <div class="mt-6">
    <IftaLabel class="mb-3 w-full">
      <InputText maxlength="100" id="txt_subject" v-model="subjectComputed"
        data-testid="kss-edit-subject"
        class="w-full h-12" :class="{'text-[var(--color-disabled)]': editable === false}"
        :readonly="editable === false"/>
      <label for="txt_subject"> {{ $t("KssFormComponent.addMeetingSubject") }}</label>
    </IftaLabel>
    <IftaLabel class="mb-3 w-full">
      <InputText id="txt_description" v-model="descriptionComputed"
        data-testid="kss-edit-description"
        class="w-full h-12" :class="{'text-[var(--color-disabled)]': editable === false}"
        :readonly="editable === false"/>
      <label for="txt_description"> {{ $t("KssFormComponent.addDescription") }}</label>
    </IftaLabel>
<!----------------- ADD TEAMS ----------------->
    <MultiSelect :modelValue="selectedTeams" display="chip" :options="teams"
      optionLabel="name" filter :placeholder="$t('KssFormComponent.addTeams')"
      class="w-full mt-1" @update:modelValue="handleTeamSelectionChange">
      <template #chip="{ value, removeCallback }">
        <div class="p-multiselect-token bg-[#f1f5f9] px-2 py-1 rounded flex items-center gap-1">
          <span>{{ value.name }}</span>
          <span class="cursor-pointer" @click="removeCallback">
            <i class="pi pi-trash"></i>
          </span>
        </div>
      </template>
    </MultiSelect>
    <!----------------- ADD PARTICIPANTS ----------------->
    <MultiSelect :modelValue="getSelectedUsers" display="chip" :options="participants"
      optionLabel="name" filter :placeholder="$t('KssFormComponent.addParticipants')"
      data-testid="kss-participants-multiselect"
      class="w-full mt-1" @update:modelValue="handleSelectionChange">
      <template #chip="{ value, removeCallback }">
        <div
          class="p-multiselect-token bg-[#f1f5f9] px-2 py-1 rounded flex items-center gap-1"
          data-testid="kss-participant-chip"
          :data-participant-id="value._id"
          :data-participant-name="value.name">
          <span>{{ value.name }}</span>
          <span class="cursor-pointer" data-testid="kss-participant-chip-remove" @click="removeCallback">
            <i class="pi pi-trash"></i>
          </span>
        </div>
      </template>
    </MultiSelect>
    <!-------------- ADD EXTERNAL PARTICIPANTS ----------->
    <edit-external-users-invite-component
      :involvedTaskUsers="involvedUsers" :allInvolvedKssUsers="allInvolvedKssUsers"
      :externalUsers="externalUsers" @updateExternalUsers="updateExternalUsers"
      class="relative mt-3 w-full"/>
  </div>
</template>

<script>
import EditExternalUsersInviteComponent
  from '@/components/Home/KssCreator/KssEditComponents/EditExternalUsersInviteComponent.vue';
import { IftaLabel, InputText, MultiSelect } from 'primevue';

export default {
  props: {
    editable: {
      type: Boolean,
    },
    initialSubject: {
      type: String,
      default: null,
    },
    initialDescription: {
      type: String,
      default: null,
    },
    involvedUsers: {
      type: Array,
      default: null,
    },
    allKssParticipants: {
      type: Array,
      default: null,
    },
    allInvolvedKssUsers: {
      type: Array,
      default: null,
    },
    selectedIds: {
      type: Array,
      default: null,
    },
    externalUsers: {
      type: Array,
      default: null,
    },
    KssTeams: {
      type: Array,
      default: null,
    },
  },
  name: 'EditKssFormComponent',
  components: {
    EditExternalUsersInviteComponent,
    IftaLabel,
    InputText,
    MultiSelect,
  },
  data() {
    return {
      showTeams: false,
      showParticipants: false,
      showEmailInvite: false,
      meetingSubject: this.initialSubject,
      description: this.initialDescription,
      countSelectedParticipants: 0,
      checkedKssTeams: [],
    };
  },
  computed: {
    /** Excludes admin/internal teams; store mutation index uses full getTeams. */
    teams() {
      return this.$store.getters.getTeamsForSelection;
    },
    /** Live store list so team selection mutations stay in sync after refetch. */
    participants() {
      return this.$store.getters.getKssParticipants;
    },
    selectedTeams() {
      return this.$store.getters.getTeams.filter((el) => el.isChecked);
    },
    selectedParticipants() {
      return this.$store.getters.getKssParticipants.filter((el) => el.isChecked);
    },
    participantsText() {
      if (this.countSelectedParticipants > 0) {
        return `${this.countSelectedParticipants} ${this.$t('KssFormComponent.selected')}`;
      }
      return this.$t('KssFormComponent.addParticipants');
    },
    getSelectedUsers() {
      return this.allKssParticipants
        .filter((el) => this.selectedIds.includes(el._id));
    },
    selectedUserIds() {
      return this.$store.getters.getKssParticipants
        .filter((el) => el.isChecked)
        .map((el) => el._id);
    },
    subjectComputed: {
      get() {
        return this.meetingSubject;
      },
      set(val) {
        this.meetingSubject = val;
        this.$emit('updateSubject', val);
      },
    },
    descriptionComputed: {
      get() {
        return this.description;
      },
      set(val) {
        this.description = val;
        this.$emit('updateDescription', val);
      },
    },
  },
  methods: {
    handleSelectionChange(newSelected) {
      const list = this.$store.getters.getKssParticipants;
      list.forEach((p) => {
        const shouldBeChecked = newSelected.some((sel) => sel._id === p._id);
        if (p.isChecked !== shouldBeChecked) {
          const index = list.findIndex((item) => item._id === p._id);
          if (index !== -1) {
            this.$store.commit('updateKssParticipantSelection', index);
          }
        }
      });
      this.updateSelectedUsers(this.selectedUserIds);
    },
    handleTeamSelectionChange(newSelected) {
      this.teams.forEach((p) => {
        const shouldBeChecked = newSelected.some((sel) => sel._id === p._id);
        if (p.isChecked !== shouldBeChecked) {
          const index = this.$store.getters.getTeams
            .findIndex((item) => item._id === p._id);
          this.$store.commit('updateKssTeamSelection', index);
        }
      });
      // has to be called to get selected ids
      this.updateSelectedUsers(this.selectedUserIds);
    },
    debounce(func, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },
    adjustEmailContainerHeight() {
      const emailContainer = this.$refs['email-container'];
      const resize = () => {
        if (emailContainer.clientHeight >= 60) {
          emailContainer.style.flexDirection = 'column';
          emailContainer.style.alignItems = 'flex-start';
        } else {
          emailContainer.style.flexDirection = 'row';
          emailContainer.style.alignItems = 'center';
        }
      };
      resize();
      const resizeHandler = this.debounce(() => resize(), 100);
      const resizeObserver = new ResizeObserver(resizeHandler);
      resizeObserver.observe(emailContainer);
    },
    toggleTeams() {
      this.showTeams = !this.showTeams;
    },
    toggleParticipants() {
      if (this.editable === false) return;
      this.showParticipants = !this.showParticipants;
    },
    toggleEmailInvite() {
      if (this.editable === false) return;
      this.showEmailInvite = !this.showEmailInvite;
    },
    updateSelectedUsers(ids) {
      this.countSelectedParticipants = ids.length;
      this.$emit('updateSelectedUserIds', ids);
    },
    updateExternalUsers(externalUsers) {
      this.$emit('updateExternalUsers', externalUsers);
    },
    updateCheckedTeams(checkedTeams) {
      this.checkedKssTeams = checkedTeams;
    },
  },
  created() {
    this.countSelectedParticipants = this.allKssParticipants
      .filter((el) => this.involvedUsers.includes(el._id)).length;

    this.KssTeams.forEach((team) => {
      const teamObject = { ...team };
      teamObject.isChecked = false;
      this.checkedKssTeams.push(teamObject);
    });
  },
  mounted() {
    // this.adjustEmailContainerHeight();
  },
};
</script>
